/**
 * 연금 복리 계산 — 순수 함수, 외부 의존성 없음
 *
 * Forward: 월 납입액 → 최종 적립액 / 월 현금흐름
 * Reverse: 목표 월 소득 → 필요 월 납입액
 */

function fvAnnuity(monthlyPayment, monthlyRate, months) {
  if (monthlyRate === 0) return monthlyPayment * months;
  return monthlyPayment * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
}

function pmtAnnuity(targetFV, monthlyRate, months) {
  if (monthlyRate === 0) return Math.round(targetFV / months);
  return Math.round(targetFV * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1));
}

export function calculateMonthlyWithdrawal(principal, annualReturnRate, months) {
  const monthlyRate = annualReturnRate / 100 / 12;
  if (monthlyRate === 0) return principal / months;
  return principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));
}

function pvWithdrawal(monthlyIncome, monthlyRate, months) {
  if (monthlyRate === 0) return monthlyIncome * months;
  return monthlyIncome * (1 - Math.pow(1 + monthlyRate, -months)) / monthlyRate;
}

function validateLifeExpectancy(retirementAge, lifeExpectancyAge) {
  if (!Number.isInteger(lifeExpectancyAge)) {
    throw new Error('기대수명은 정수 나이로 입력해주세요.');
  }
  if (lifeExpectancyAge <= retirementAge) {
    throw new Error('기대수명은 은퇴 나이보다 커야 합니다.');
  }
  if (lifeExpectancyAge > 150) {
    throw new Error('기대수명은 150세 이하로 입력해주세요.');
  }
}

function buildDepletionDetails(principal, monthlyWithdrawal, monthlyRate, retirementAge, lifeExpectancyAge) {
  const details = [];
  let balance = principal;
  const years = lifeExpectancyAge - retirementAge;

  for (let year = 1; year <= years; year++) {
    for (let month = 1; month <= 12; month++) {
      balance = balance * (1 + monthlyRate) - monthlyWithdrawal;
    }

    details.push({
      year,
      age: retirementAge + year,
      monthly_pension: Math.round(monthlyWithdrawal),
      remaining_balance: Math.max(0, Math.round(balance)),
    });
  }

  if (details.length > 0) {
    details[details.length - 1].remaining_balance = 0;
  }

  return details;
}

/**
 * @param {{ current_age, retirement_age, monthly_payment, annual_return_rate, pension_yield_rate, life_expectancy_age, initial_principal? }} params
 * @returns {{
 *   final_accumulated_amount, monthly_cash_flow, total_paid_amount, cash_flow_details,
 *   depletion_monthly_pension, depletion_withdrawal_months, depletion_withdrawal_years,
 *   depletion_start_age, depletion_end_age, depletion_total_payout, depletion_total_interest,
 *   depletion_details
 * }}
 */
export function calculateForward(params) {
  const {
    current_age,
    retirement_age,
    monthly_payment,
    annual_return_rate,
    pension_yield_rate,
    life_expectancy_age = 100,
    initial_principal = 0,
  } = params;

  const years = retirement_age - current_age;
  const totalMonths = years * 12;
  const r = annual_return_rate / 100 / 12;
  validateLifeExpectancy(retirement_age, life_expectancy_age);

  const fvInitial = r === 0
    ? initial_principal
    : initial_principal * Math.pow(1 + r, totalMonths);
  const final_accumulated_amount = Math.round(fvAnnuity(monthly_payment, r, totalMonths) + fvInitial);
  const monthly_cash_flow = Math.round(final_accumulated_amount * pension_yield_rate / 100 / 12);
  const total_paid_amount = monthly_payment * totalMonths + initial_principal;
  const depletion_withdrawal_months = (life_expectancy_age - retirement_age) * 12;
  const depletionMonthlyPensionExact = calculateMonthlyWithdrawal(
    final_accumulated_amount,
    pension_yield_rate,
    depletion_withdrawal_months
  );
  const depletion_monthly_pension = Math.round(depletionMonthlyPensionExact);
  const depletion_total_payout = Math.round(
    depletionMonthlyPensionExact * depletion_withdrawal_months
  );
  const depletion_total_interest = depletion_total_payout - final_accumulated_amount;
  const depletion_details = buildDepletionDetails(
    final_accumulated_amount,
    depletionMonthlyPensionExact,
    pension_yield_rate / 100 / 12,
    retirement_age,
    life_expectancy_age
  );

  const cash_flow_details = [];
  for (let year = 1; year <= years; year++) {
    const months = year * 12;
    const fvInitialYear = r === 0
      ? initial_principal
      : initial_principal * Math.pow(1 + r, months);
    const accumulated_amount = Math.round(fvAnnuity(monthly_payment, r, months) + fvInitialYear);
    const paid_amount = monthly_payment * months + initial_principal;
    const interest_earned = accumulated_amount - paid_amount;
    cash_flow_details.push({
      year,
      age: current_age + year,
      accumulated_amount,
      paid_amount,
      interest_earned,
    });
  }

  return {
    final_accumulated_amount,
    monthly_cash_flow,
    total_paid_amount,
    cash_flow_details,
    depletion_monthly_pension,
    depletion_withdrawal_months,
    depletion_withdrawal_years: life_expectancy_age - retirement_age,
    depletion_start_age: retirement_age,
    depletion_end_age: life_expectancy_age,
    depletion_total_payout,
    depletion_total_interest,
    depletion_details,
  };
}

/**
 * @param {{ current_age, retirement_age, desired_monthly_income, annual_return_rate, pension_yield_rate, life_expectancy_age, initial_principal? }} params
 * @returns {{
 *   interest_only_available, interest_only_unavailable_reason, required_final_amount,
 *   required_monthly_saving, total_saving_amount, expected_interest_earned,
 *   total_payment_period, savings_details, depletion_required_final_amount,
 *   depletion_required_monthly_saving, depletion_total_saving_amount,
 *   depletion_expected_interest_earned, depletion_withdrawal_months,
 *   depletion_withdrawal_years, depletion_start_age, depletion_end_age,
 *   required_final_amount_reduction, monthly_saving_reduction
 * }}
 */
export function calculateReverse(params) {
  const {
    current_age,
    retirement_age,
    desired_monthly_income,
    annual_return_rate,
    pension_yield_rate,
    life_expectancy_age = 100,
    initial_principal = 0,
  } = params;

  const years = retirement_age - current_age;
  const totalMonths = years * 12;
  const r = annual_return_rate / 100 / 12;
  const withdrawalMonthlyRate = pension_yield_rate / 100 / 12;
  validateLifeExpectancy(retirement_age, life_expectancy_age);

  const fvInitial = r === 0
    ? initial_principal
    : initial_principal * Math.pow(1 + r, totalMonths);

  const interest_only_available = pension_yield_rate > 0;
  const interest_only_unavailable_reason = interest_only_available
    ? ''
    : '원금 보존 방식은 이자/수익률이 0%이면 계산할 수 없습니다.';
  const required_final_amount = interest_only_available
    ? Math.round(desired_monthly_income * 1200 / pension_yield_rate)
    : null;
  const interestOnlyNeedSaving = interest_only_available
    ? Math.max(0, required_final_amount - fvInitial)
    : null;
  const required_monthly_saving = interest_only_available
    ? pmtAnnuity(interestOnlyNeedSaving, r, totalMonths)
    : null;
  const total_saving_amount = interest_only_available ? required_monthly_saving * totalMonths : null;
  // Actual retirement amount: if initial principal already covers the goal, monthly_saving = 0
  // and the user accumulates fvInitial (which may exceed required_final_amount).
  const io_actual_final = interest_only_available
    ? (interestOnlyNeedSaving <= 0 ? Math.round(fvInitial) : required_final_amount)
    : null;
  const expected_interest_earned = interest_only_available
    ? io_actual_final - (total_saving_amount || 0) - initial_principal
    : null;
  const total_payment_period = years;
  const depletion_withdrawal_months = (life_expectancy_age - retirement_age) * 12;
  const depletion_required_final_amount = Math.round(
    pvWithdrawal(desired_monthly_income, withdrawalMonthlyRate, depletion_withdrawal_months)
  );
  const depletionNeedSaving = Math.max(0, depletion_required_final_amount - fvInitial);
  const depletion_required_monthly_saving = pmtAnnuity(depletionNeedSaving, r, totalMonths);
  const depletion_total_saving_amount = depletion_required_monthly_saving * totalMonths;
  const depletion_actual_final = depletionNeedSaving <= 0
    ? Math.round(fvInitial)
    : depletion_required_final_amount;
  const depletion_expected_interest_earned =
    depletion_actual_final - depletion_total_saving_amount - initial_principal;
  const required_final_amount_reduction = interest_only_available
    ? required_final_amount - depletion_required_final_amount
    : null;
  const monthly_saving_reduction = interest_only_available
    ? required_monthly_saving - depletion_required_monthly_saving
    : null;

  const savings_details = [];
  if (interest_only_available) {
    for (let year = 1; year <= years; year++) {
      const months = year * 12;
      const isLast = year === years;
      const fvInitialYear = r === 0 ? initial_principal : initial_principal * Math.pow(1 + r, months);
      // PMT 반올림 오차 보정: 월 저축이 있을 때만 마지막 연도를 목표액으로 고정.
      // 초기 자금만으로 목표를 초과하는 경우(monthly_saving=0)에는 실제 FV를 그대로 사용해
      // 마지막 연도가 첫 연도보다 낮아지는 역행 현상을 방지한다.
      const accumulated_amount = (isLast && required_monthly_saving > 0)
        ? required_final_amount
        : Math.round(fvAnnuity(required_monthly_saving, r, months) + fvInitialYear);
      const paid_amount = required_monthly_saving * months + initial_principal;
      const interest_earned = accumulated_amount - paid_amount;
      const progress_percentage = isLast
        ? 100
        : Math.round((accumulated_amount / required_final_amount) * 1000) / 10;
      savings_details.push({
        year,
        age: current_age + year,
        accumulated_amount,
        interest_earned,
        progress_percentage,
      });
    }
  }

  return {
    interest_only_available,
    interest_only_unavailable_reason,
    required_final_amount,
    required_monthly_saving,
    total_saving_amount,
    expected_interest_earned,
    total_payment_period,
    savings_details,
    depletion_required_final_amount,
    depletion_required_monthly_saving,
    depletion_total_saving_amount,
    depletion_expected_interest_earned,
    depletion_withdrawal_months,
    depletion_withdrawal_years: life_expectancy_age - retirement_age,
    depletion_start_age: retirement_age,
    depletion_end_age: life_expectancy_age,
    required_final_amount_reduction,
    monthly_saving_reduction,
  };
}
